import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { authAPI } from '../../api/auth';
import { useUserOverviewPairs } from '../../hooks/useUserOverviewPairs';

const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const StatCard = styled.div`
  background: ${(p) => p.theme.surface};
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${(p) => p.theme.shadowSm};
  border: 1px solid ${(p) => p.theme.border};
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(p) => p.theme.shadowMd};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => p.theme.textMuted};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(p) => p.theme.textPrimary};
  margin-bottom: 8px;
`;

const StatValueSecondary = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  margin-top: 6px;
  line-height: 1.3;
`;

const StatChange = styled.div`
  font-size: 14px;
  color: ${(props) => (props.positive ? props.theme.positive : props.theme.negative)};
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${(p) => p.theme.surface};
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${(p) => p.theme.shadowSm};
  border: 1px solid ${(p) => p.theme.border};
  transition: background 0.2s ease, border-color 0.2s ease;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => p.theme.textPrimary};
  margin: 0 0 20px 0;
`;

const ChartToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const ChartRangeGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ChartRangeButton = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? p.theme.navAccent : p.theme.border)};
  background: ${(p) =>
    p.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : p.theme.surface};
  color: ${(p) => (p.$active ? '#fff' : p.theme.textSecondary)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s ease;

  &:hover {
    border-color: ${(p) => p.theme.navAccent};
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
  }
`;

const ChartHint = styled.span`
  font-size: 12px;
  color: ${(p) => p.theme.textMuted};
`;

const ChartArea = styled.div`
  width: 100%;
  height: 300px;
`;

const ChartEmpty = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.textMuted};
  font-size: 14px;
  background: ${(p) => p.theme.surfaceMuted};
  border-radius: 12px;
`;

const RecentActivity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 2px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${(p) => p.theme.surfaceMuted};
  border-radius: 8px;
  transition: background 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    color: ${(p) => p.theme.navAccent};
  }
`;

const ActivityText = styled.div`
  flex: 1;
  min-width: 0;

  div:first-child {
    font-weight: 500;
    color: ${(p) => p.theme.textPrimary};
    font-size: 14px;
  }

  div:last-child {
    color: ${(p) => p.theme.textMuted};
    font-size: 12px;
    line-height: 1.4;
    margin-top: 2px;
  }
`;

const ActivityProfit = styled.span`
  color: ${(p) => (p.$positive ? p.theme.positive : p.theme.negative)};
  font-weight: 600;
`;

const OverviewError = styled.div`
  color: ${(p) => p.theme.errorText};
  font-size: 14px;
  margin-bottom: 8px;
`;

const SourceFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 16px;
  background: ${(p) => p.theme.surface};
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.border};
  box-shadow: ${(p) => p.theme.shadowSm};
`;

const SourceFilterLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textSecondary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s ease, color 0.2s ease;
  margin-left: auto;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    border-color: ${(p) => p.theme.navAccent};
    color: ${(p) => p.theme.textPrimary};
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${(p) =>
    p.$spinning &&
    `
    svg {
      animation: overviewRefreshSpin 0.8s linear infinite;
    }
  `}

  @keyframes overviewRefreshSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SourceFilterLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme.textMuted};
  margin-right: 4px;
`;

const SourceFilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const formatQty = (n, maxFrac = 2) => {
  if (n == null || Number.isNaN(Number(n))) return ' - ';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFrac,
  }).format(Number(n));
};

const formatSignedProfit = (n, maxFrac = 6) => {
  if (n == null || Number.isNaN(Number(n))) return ' - ';
  const v = Number(n);
  const sign = v >= 0 ? '+' : '−';
  return `${sign}${formatQty(Math.abs(v), maxFrac)}`;
};

const getPlatformOverview = (overview, platform) => {
  if (!overview) return null;
  if (platform === 'mexc') return overview.mexc;
  if (platform === 'binance') return overview.binance;
  if (platform === 'okx') return overview.okx;
  return null;
};

const formatRelativeTime = (tsMs) => {
  if (tsMs == null || Number.isNaN(Number(tsMs))) return '';
  const diff = Math.max(0, Date.now() - Number(tsMs));
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const week = Math.floor(day / 7);
  if (week < 8) return `${week}wk ago`;
  const month = Math.floor(day / 30);
  return `${Math.max(1, month)}mo ago`;
};

const Overview = () => {
  const theme = useTheme();
  const { userOverviewPairs, loading: overviewPairsLoading } = useUserOverviewPairs();
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [profitChartRange, setProfitChartRange] = useState('30d');
  const [dataSource, setDataSource] = useState('all');

  const sourceOptions = useMemo(
    () => [
      { id: 'all', label: 'All' },
      ...userOverviewPairs.map((p) => ({ id: p.sourceId, label: p.overviewLabel })),
    ],
    [userOverviewPairs]
  );

  const selectedPair = useMemo(
    () => userOverviewPairs.find((p) => p.sourceId === dataSource) || null,
    [userOverviewPairs, dataSource]
  );

  useEffect(() => {
    if (overviewPairsLoading) return;
    const valid = new Set(['all', ...userOverviewPairs.map((p) => p.sourceId)]);
    if (!valid.has(dataSource)) {
      setDataSource('all');
    }
  }, [userOverviewPairs, overviewPairsLoading, dataSource]);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const res = await authAPI.getDashboardOverview(dataSource);
      if (res.success && res.data) {
        setOverview(res.data);
      } else {
        setOverviewError(res.message || 'Could not load overview');
        setOverview(null);
      }
    } catch (e) {
      setOverviewError(e.response?.data?.message || e.message || 'Could not load overview');
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [dataSource]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const pnl24 = overview?.pnl?.['24h'];
  const pnl7d = overview?.pnl?.['7d'];
  const pnl30d = overview?.pnl?.['30d'];

  const profitChartData = useMemo(() => {
    const series = overview?.profitSeries;
    if (!series) return [];
    return profitChartRange === '30d' ? series.last30Days || [] : series.last12Months || [];
  }, [overview, profitChartRange]);

  const formatChartX = (label) => {
    if (!label || typeof label !== 'string') return label;
    if (profitChartRange === '30d') {
      const parts = label.split('-');
      if (parts.length >= 3) return `${parts[1]}/${parts[2]}`;
    }
    return label;
  };

  const chartTooltipFormatter = (value) => formatSignedProfit(Number(value), 8);

  let balanceSubtitle = 'Connect keys in Settings';
  if (overview) {
    if (selectedPair) {
      const connected = getPlatformOverview(overview, selectedPair.platform)?.connected;
      balanceSubtitle = connected ? selectedPair.overviewLabel : balanceSubtitle;
    } else {
      const parts = userOverviewPairs
        .map((pair) => {
          const connected = getPlatformOverview(overview, pair.platform)?.connected;
          return connected ? pair.overviewLabel : null;
        })
        .filter(Boolean);
      balanceSubtitle = parts.join(' · ') || balanceSubtitle;
    }
  }

  const balanceValueMain = overviewLoading
    ? '…'
    : selectedPair?.isEurPair
      ? `€${formatQty(overview?.eur_euri_total ?? 0, 4)}`
      : '$' + formatQty(overview?.usdt_usdc_total ?? 0, 4);

  const balanceValueSub =
    overviewLoading || dataSource !== 'all'
      ? null
      : `€${formatQty(overview?.eur_euri_total ?? 0, 4)}`;

  const stats = [
    {
      title: 'Total Balance',
      valueMain: balanceValueMain,
      valueSub: balanceValueSub,
      change: overviewLoading ? 'Loading…' : `Spot free+locked · ${balanceSubtitle}`,
      positive: true,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23"></line>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      title: '24h P&L',
      valueMain: overviewLoading ? '…' : formatSignedProfit(pnl24),
      valueSub: null,
      change: 'Σ profit',
      positive: overviewLoading || (pnl24 ?? 0) >= 0,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
          <polyline points="17 6 23 6 23 12"></polyline>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
    },
    {
      title: '7d P&L',
      valueMain: overviewLoading ? '…' : formatSignedProfit(pnl7d),
      valueSub: null,
      change: 'Σ profit',
      positive: overviewLoading || (pnl7d ?? 0) >= 0,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <polygon points="10,8 16,12 10,16 10,8"></polygon>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)',
    },
    {
      title: '30d P&L',
      valueMain: overviewLoading ? '…' : formatSignedProfit(pnl30d),
      valueSub: null,
      change: 'Σ profit',
      positive: overviewLoading || (pnl30d ?? 0) >= 0,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22,4 12,14.01 9,11.01"></polyline>
        </svg>
      ),
      bgColor: 'linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)',
    },
  ];

  return (
    <OverviewContainer>
      <SourceFilterBar>
        <SourceFilterLeft>
          <SourceFilterLabel>Show</SourceFilterLabel>
          <SourceFilterGroup>
            {sourceOptions.map((opt) => (
              <ChartRangeButton
                key={opt.id}
                type="button"
                $active={dataSource === opt.id}
                onClick={() => setDataSource(opt.id)}
              >
                {opt.label}
              </ChartRangeButton>
            ))}
          </SourceFilterGroup>
        </SourceFilterLeft>
        <RefreshButton
          type="button"
          onClick={() => loadOverview()}
          disabled={overviewLoading}
          $spinning={overviewLoading}
          aria-label="Refresh overview"
          title="Refresh overview"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          Refresh
        </RefreshButton>
      </SourceFilterBar>
      {overviewError && <OverviewError>{overviewError}</OverviewError>}
      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatHeader>
              <StatTitle>{stat.title}</StatTitle>
              <StatIcon bgColor={stat.bgColor}>
                {stat.icon}
              </StatIcon>
            </StatHeader>
            <StatValue>
              {stat.valueMain}
              {stat.valueSub != null && stat.valueSub !== '' && (
                <StatValueSecondary>
                  {stat.valueSub}
                </StatValueSecondary>
              )}
            </StatValue>
            <StatChange positive={stat.positive}>
              {stat.positive ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
                  <polyline points="17 18 23 18 23 12"></polyline>
                </svg>
              )}
              {stat.change}
            </StatChange>
          </StatCard>
        ))}
      </StatsGrid>

      <ChartSection>
        <ChartCard>
          <ChartToolbar>
            <ChartTitle style={{ margin: 0 }}>Portfolio Performance</ChartTitle>
            <ChartRangeGroup>
              <ChartRangeButton
                type="button"
                $active={profitChartRange === '30d'}
                onClick={() => setProfitChartRange('30d')}
              >
                Last 30 days
              </ChartRangeButton>
              <ChartRangeButton
                type="button"
                $active={profitChartRange === '12m'}
                onClick={() => setProfitChartRange('12m')}
              >
                Last 12 months
              </ChartRangeButton>
            </ChartRangeGroup>
          </ChartToolbar>
          <ChartHint>
            {profitChartRange === '30d'
              ? 'Daily profit'
              : 'Monthly profit'}
          </ChartHint>
          {overviewLoading ? (
            <ChartEmpty>Loading chart…</ChartEmpty>
          ) : profitChartData.length === 0 ? (
            <ChartEmpty>No profit data in this range</ChartEmpty>
          ) : (
            <ChartArea>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={profitChartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.chartGrid} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: theme.chartTick }}
                    tickFormatter={formatChartX}
                    interval={profitChartRange === '30d' ? 2 : 0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: theme.chartTick }}
                    width={56}
                    tickFormatter={(v) => formatQty(v, 4)}
                  />
                  <Tooltip
                    formatter={chartTooltipFormatter}
                    labelFormatter={(label) => (profitChartRange === '30d' ? `Day ${label}` : `Month ${label}`)}
                    contentStyle={{
                      borderRadius: 8,
                      border: `1px solid ${theme.tooltipBorder}`,
                      background: theme.tooltipBg,
                      color: theme.textPrimary,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="profit"
                    name="Profit"
                    stroke={theme.lineChart}
                    strokeWidth={2}
                    dot={{ r: 3, fill: theme.lineChart }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartArea>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle>Recent Activity</ChartTitle>
          {overviewLoading ? (
            <ChartEmpty style={{ height: 'auto', minHeight: 140, marginTop: 8 }}>Loading orders…</ChartEmpty>
          ) : !overview?.recentOrders?.length ? (
            <ChartEmpty style={{ height: 'auto', minHeight: 140, marginTop: 8 }}>No stored orders yet</ChartEmpty>
          ) : (
            <RecentActivity>
              {overview.recentOrders.map((o, index) => {
                const isBuy = String(o.side).toUpperCase() === 'BUY';
                const profitNum = o.profit != null && Number.isFinite(Number(o.profit)) ? Number(o.profit) : null;
                return (
                  <ActivityItem key={`${o.time}-${o.symbol}-${o.side}-${index}`}>
                    {isBuy ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12l7 7 7-7" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                    )}
                    <ActivityText>
                      <div>
                        {o.side} {o.symbol}
                        {o.platform ? (
                          <span style={{ color: theme.textSubtle, fontWeight: 400 }}> · {o.platform}</span>
                        ) : null}
                        {o.status ? (
                          <span style={{ color: theme.textSubtle, fontWeight: 400 }}> · {o.status}</span>
                        ) : null}
                      </div>
                      <div>
                        Amt {formatQty(o.executedQty, 6)} · Price {formatQty(o.price, 8)}
                        {profitNum != null ? (
                          <>
                            {' · '}
                            Profit{' '}
                            <ActivityProfit $positive={profitNum >= 0}>
                              {formatSignedProfit(profitNum, 8)}
                            </ActivityProfit>
                          </>
                        ) : null}
                        {o.time != null ? (
                          <span style={{ color: theme.textSubtle }}> · {formatRelativeTime(o.time)}</span>
                        ) : null}
                      </div>
                    </ActivityText>
                  </ActivityItem>
                );
              })}
            </RecentActivity>
          )}
        </ChartCard>
      </ChartSection>
    </OverviewContainer>
  );
};

export default Overview;
